import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaFileInvoiceDollar, FaCheckCircle, FaCalculator, FaFileUpload, FaBook } from 'react-icons/fa';
import Swal from 'sweetalert2';
import retencionHonorariosService from '../../services/retencionHonorariosService';
import YearSelector from '../../components/common/YearSelector';
import useYearSelector from '../../hooks/useYearSelector';
import { normalizarRut } from '../../utils/rutUtils';
import '../../styles/pages/RRHH/RetencionHonorariosPage.css';

const MESES = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
];

const ESTADOS = [
    { value: 'pendiente', label: 'Pendiente', color: 'warning' },
    { value: 'pagado', label: 'Pagado', color: 'success' },
    { value: 'anulado', label: 'Anulado', color: 'danger' }
];

/**
 * Página de Retención de Honorarios
 * Gestión de boletas de honorarios de profesionales terceros
 * Preparado para MCP/Agentes autónomos
 */
const RetencionHonorariosPage = ({ token, id_company }) => {
    const [retenciones, setRetenciones] = useState([]);
    const [profesionales, setProfesionales] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Referencia para el input file
    const fileInputRef = useRef(null);

    // Selector de año y mes
    const { selectedYear, setSelectedYear, yearOptions } = useYearSelector(null, 10, 1);
    const [selectedMes, setSelectedMes] = useState('');

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroProfesional, setFiltroProfesional] = useState('');

    // Formulario
    const [formData, setFormData] = useState({
        rut_profesional: '',
        nombre_profesional: '',
        numero_boleta: '',
        fecha_emision: '',
        monto_bruto: '',
        tasa_retencion: '11.50',
        monto_retencion: '0',
        monto_liquido: '0',
        estado: 'pendiente'
    });

    useEffect(() => {
        fetchProfesionales();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            fetchRetenciones();
            fetchResumen();
        }
    }, [filtroEstado, filtroProfesional, selectedYear, selectedMes]);

    const fetchRetenciones = async () => {
        try {
            setLoading(true);
            const params = {
                id_company,
                anio: selectedYear
            };

            if (selectedMes) params.mes = selectedMes;
            if (filtroEstado) params.estado = filtroEstado;
            if (filtroProfesional) params.rut_profesional = filtroProfesional;

            const data = await retencionHonorariosService.getRetencionesHonorarios(params, token);
            setRetenciones(data);
        } catch (error) {
            console.error('Error al cargar retenciones:', error);
            Swal.fire('Error', 'No se pudieron cargar las retenciones', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchResumen = async () => {
        try {
            const data = await retencionHonorariosService.getResumen(
                selectedYear,
                selectedMes || null,
                id_company,
                token
            );
            setResumen(data);
        } catch (error) {
            console.error('Error al cargar resumen:', error);
        }
    };

    const fetchProfesionales = async () => {
        try {
            const data = await retencionHonorariosService.getProfesionales(id_company, token);
            setProfesionales(data);
        } catch (error) {
            console.error('Error al cargar profesionales:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Calcular automáticamente la retención cuando cambia el monto o la tasa
        if (name === 'monto_bruto' || name === 'tasa_retencion') {
            const monto = name === 'monto_bruto' ? parseFloat(value) : parseFloat(formData.monto_bruto);
            const tasa = name === 'tasa_retencion' ? parseFloat(value) : parseFloat(formData.tasa_retencion);

            if (!isNaN(monto) && !isNaN(tasa)) {
                const retencion = (monto * (tasa / 100)).toFixed(2);
                const liquido = (monto - parseFloat(retencion)).toFixed(2);

                setFormData(prev => ({
                    ...prev,
                    monto_retencion: retencion,
                    monto_liquido: liquido
                }));
            }
        }
    };

    const handleProfesionalChange = (e) => {
        const rut = e.target.value;
        setFormData(prev => ({
            ...prev,
            rut_profesional: rut
        }));

        // Auto-completar datos del profesional si ya existe (normalizar RUT para comparación)
        const rutNormalizado = normalizarRut(rut);
        const profesional = profesionales.find(p => normalizarRut(p.rut_profesional) === rutNormalizado);
        if (profesional) {
            setFormData(prev => ({
                ...prev,
                nombre_profesional: profesional.nombre_profesional
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const retencionData = {
                ...formData,
                id_company,
                monto_bruto: parseFloat(formData.monto_bruto),
                tasa_retencion: parseFloat(formData.tasa_retencion)
            };

            if (editingId) {
                await retencionHonorariosService.updateRetencionHonorario(editingId, retencionData, token);
                Swal.fire('Éxito', 'Retención actualizada correctamente', 'success');
            } else {
                await retencionHonorariosService.createRetencionHonorario(retencionData, token);
                Swal.fire('Éxito', 'Retención registrada correctamente', 'success');
            }

            resetForm();
            fetchRetenciones();
            fetchResumen();
            fetchProfesionales();
        } catch (error) {
            console.error('Error al guardar retención:', error);
            Swal.fire('Error', error.message || 'Error al guardar retención', 'error');
        }
    };

    const handleEdit = (retencion) => {
        setFormData({
            rut_profesional: retencion.rut_profesional,
            nombre_profesional: retencion.nombre_profesional,
            numero_boleta: retencion.numero_boleta || '',
            fecha_emision: retencion.fecha_emision ? retencion.fecha_emision.split('T')[0] : '',
            monto_bruto: retencion.monto_bruto,
            tasa_retencion: retencion.tasa_retencion,
            monto_retencion: retencion.monto_retencion,
            monto_liquido: retencion.monto_liquido,
            estado: retencion.estado_pago || 'pendiente'
        });
        setEditingId(retencion.id_retencion);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await retencionHonorariosService.deleteRetencionHonorario(id, id_company, token);
                Swal.fire('Eliminado', 'Retención eliminada correctamente', 'success');
                fetchRetenciones();
                fetchResumen();
                fetchProfesionales();
            } catch (error) {
                console.error('Error al eliminar:', error);
                Swal.fire('Error', error.message || 'Error al eliminar retención', 'error');
            }
        }
    };

    const handleMarcarPagado = async (id) => {
        const { value: formValues } = await Swal.fire({
            title: 'Marcar como Pagado',
            html:
                '<label>Fecha de Pago:</label>' +
                '<input id="swal-fecha" type="date" class="swal2-input" required>' +
                '<label>Forma de Pago:</label>' +
                '<select id="swal-forma" class="swal2-input">' +
                '<option value="transferencia">Transferencia</option>' +
                '<option value="cheque">Cheque</option>' +
                '<option value="efectivo">Efectivo</option>' +
                '</select>',
            focusConfirm: false,
            preConfirm: () => {
                return {
                    fecha_pago: document.getElementById('swal-fecha').value,
                    forma_pago: document.getElementById('swal-forma').value
                };
            }
        });

        if (formValues) {
            try {
                await retencionHonorariosService.cambiarEstado(
                    id,
                    {
                        id_company,
                        estado: 'pagado',
                        ...formValues
                    },
                    token
                );
                Swal.fire('Éxito', 'Retención marcada como pagada', 'success');
                fetchRetenciones();
                fetchResumen();
            } catch (error) {
                console.error('Error al marcar pagado:', error);
                Swal.fire('Error', error.message || 'Error al cambiar estado', 'error');
            }
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validar que sea un archivo .xls o .xlsx
        const validExtensions = ['.xls', '.xlsx'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            Swal.fire('Error', 'Por favor seleccione un archivo de Excel (.xls o .xlsx)', 'error');
            event.target.value = '';
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('id_company', id_company);

            Swal.fire({
                title: 'Procesando archivo...',
                text: 'Por favor espere mientras se cargan los honorarios',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            await retencionHonorariosService.uploadHonorariosMensuales(formData, token);

            Swal.fire('Éxito', 'Honorarios mensuales cargados correctamente', 'success');
            fetchRetenciones();
            fetchResumen();
            fetchProfesionales();
        } catch (error) {
            console.error('Error al cargar archivo:', error);
            Swal.fire('Error', error.message || 'Error al cargar el archivo de honorarios', 'error');
        } finally {
            // Limpiar el input file para permitir seleccionar el mismo archivo nuevamente
            event.target.value = '';
        }
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleCrearAsientos = async () => {
        if (!selectedMes) {
            Swal.fire('Atención', 'Debe seleccionar un mes para generar los asientos contables', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Crear Asientos Contables',
            html: `
                <p>Se crearán los asientos contables para todas las retenciones de honorarios de <strong>${MESES.find(m => m.value === selectedMes)?.label} ${selectedYear}</strong> que aún no tengan asiento generado.</p>
                <br/>
                <p><strong>Asientos a generar:</strong></p>
                <ul style="text-align: left; margin-top: 10px;">
                    <li>DEBE: Gasto en Honorarios</li>
                    <li>HABER: Retención Honorarios por Pagar</li>
                    <li>HABER: Honorarios por Pagar</li>
                </ul>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Crear Asientos',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Procesando...',
                    text: 'Creando asientos contables',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await retencionHonorariosService.crearAsientosContables({
                    id_company,
                    mes: parseInt(selectedMes),
                    anio: selectedYear
                }, token);

                Swal.fire({
                    title: 'Asientos Creados',
                    html: `
                        <p>Se procesaron <strong>${response.data.retenciones_procesadas}</strong> retenciones.</p>
                        <br/>
                        <table style="width: 100%; text-align: left; font-size: 14px;">
                            <tr><td>Monto Bruto:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_bruto)}</td></tr>
                            <tr><td>Retención:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_retencion)}</td></tr>
                            <tr><td>Monto Líquido:</td><td style="text-align: right;">${formatCurrency(response.data.totales.monto_liquido)}</td></tr>
                        </table>
                    `,
                    icon: 'success'
                });

                fetchRetenciones();
            } catch (error) {
                console.error('Error al crear asientos:', error);
                Swal.fire('Error', error.message || 'Error al crear asientos contables', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            rut_profesional: '',
            nombre_profesional: '',
            numero_boleta: '',
            fecha_emision: '',
            monto_bruto: '',
            tasa_retencion: '11.50',
            monto_retencion: '0',
            monto_liquido: '0',
            estado: 'pendiente'
        });
        setEditingId(null);
        setShowModal(false);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(value);
    };

    return (
        <div className="retencion-honorarios-page">
            <div className="page-header">
                <div className="header-left">
                    <FaFileInvoiceDollar className="page-icon" />
                    <div>
                        <h1>Retención de Honorarios</h1>
                        <p>Gestión de boletas de profesionales terceros</p>
                    </div>
                </div>
                <div className="header-right">
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        label="Año"
                        showIcon={true}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <button className="btn-primary" onClick={handleUploadButtonClick}>
                        <FaFileUpload /> Cargar Honorarios Mensuales
                    </button>
                    <button className="btn-secondary" onClick={handleCrearAsientos} title="Crear asientos contables del mes seleccionado">
                        <FaBook /> Crear Asientos Contables
                    </button>
                </div>
            </div>

            {/* Resumen */}
            {resumen && (
                <div className="resumen-cards">
                    <div className="resumen-card">
                        <h3>Total Retenciones</h3>
                        <p className="value">{resumen.total_retenciones}</p>
                    </div>
                    <div className="resumen-card warning">
                        <h3>Pendientes</h3>
                        <p className="value">{resumen.pendientes}</p>
                        <p className="sub-value">{formatCurrency(resumen.monto_pendiente)}</p>
                    </div>
                    <div className="resumen-card success">
                        <h3>Pagadas</h3>
                        <p className="value">{resumen.pagadas}</p>
                        <p className="sub-value">{formatCurrency(resumen.monto_pagado)}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Total Honorarios</h3>
                        <p className="value">{formatCurrency(resumen.total_monto_bruto)}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Total Retenciones</h3>
                        <p className="value">{formatCurrency(resumen.total_retenciones_monto)}</p>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="filters">
                <div className="filter-group">
                    <label>Mes:</label>
                    <select value={selectedMes} onChange={(e) => setSelectedMes(e.target.value)}>
                        <option value="">Todos</option>
                        {MESES.map(mes => (
                            <option key={mes.value} value={mes.value}>{mes.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Estado:</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        {ESTADOS.map(estado => (
                            <option key={estado.value} value={estado.value}>{estado.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Profesional:</label>
                    <select value={filtroProfesional} onChange={(e) => setFiltroProfesional(e.target.value)}>
                        <option value="">Todos</option>
                        {profesionales.map(prof => (
                            <option key={prof.rut_profesional} value={prof.rut_profesional}>
                                {prof.nombre_profesional}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de retenciones */}
            <div className="table-container">
                {loading ? (
                    <div className="loading">Cargando retenciones...</div>
                ) : retenciones.length === 0 ? (
                    <div className="empty-state">
                        <FaFileInvoiceDollar size={48} />
                        <p>No hay retenciones registradas para este filtro</p>
                    </div>
                ) : (
                    <table className="retencion-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Profesional</th>
                                <th>N° Boleta</th>
                                <th>Monto Bruto</th>
                                <th>Retención</th>
                                <th>Monto Líquido</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {retenciones.map(retencion => (
                                <tr key={retencion.id_retencion}>
                                    <td>{new Date(retencion.fecha_emision).toLocaleDateString('es-CL')}</td>
                                    <td>
                                        {retencion.nombre_profesional}
                                        <div className="sub-text">{retencion.rut_profesional}</div>
                                    </td>
                                    <td>{retencion.numero_boleta || '-'}</td>
                                    <td className="currency">{formatCurrency(retencion.monto_bruto)}</td>
                                    <td className="currency">
                                        {formatCurrency(retencion.monto_retencion)}
                                        <div className="sub-text">{retencion.tasa_retencion}%</div>
                                    </td>
                                    <td className="currency">{formatCurrency(retencion.monto_liquido)}</td>
                                    <td>
                                        <span className={`badge badge-${ESTADOS.find(e => e.value === retencion.estado_pago)?.color}`}>
                                            {retencion.estado_pago}
                                        </span>
                                        {retencion.fecha_pago && (
                                            <div className="sub-text">
                                                Pagado: {new Date(retencion.fecha_pago).toLocaleDateString('es-CL')}
                                            </div>
                                        )}
                                    </td>
                                    <td className="actions">
                                        {retencion.estado_pago === 'pendiente' && (
                                            <button
                                                className="btn-icon btn-success"
                                                onClick={() => handleMarcarPagado(retencion.id_retencion)}
                                                title="Marcar como pagado"
                                            >
                                                <FaCheckCircle />
                                            </button>
                                        )}
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleEdit(retencion)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(retencion.id_retencion)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Editar Retención' : 'Nueva Retención de Honorarios'}</h2>
                            <button className="close-btn" onClick={resetForm}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>RUT Profesional *</label>
                                    <select
                                        name="rut_profesional"
                                        value={formData.rut_profesional}
                                        onChange={handleProfesionalChange}
                                        required
                                    >
                                        <option value="">Seleccione o ingrese nuevo...</option>
                                        {profesionales.map(prof => (
                                            <option key={prof.rut_profesional} value={prof.rut_profesional}>
                                                {prof.rut_profesional} - {prof.nombre_profesional}
                                            </option>
                                        ))}
                                    </select>
                                    <small>O ingrese manualmente en el siguiente campo</small>
                                    <input
                                        type="text"
                                        name="rut_profesional"
                                        value={formData.rut_profesional}
                                        onChange={handleInputChange}
                                        placeholder="12.345.678-9"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Nombre Profesional *</label>
                                    <input
                                        type="text"
                                        name="nombre_profesional"
                                        value={formData.nombre_profesional}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>N° Boleta</label>
                                    <input
                                        type="text"
                                        name="numero_boleta"
                                        value={formData.numero_boleta}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Emisión *</label>
                                    <input
                                        type="date"
                                        name="fecha_emision"
                                        value={formData.fecha_emision}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Estado</label>
                                    <select
                                        name="estado"
                                        value={formData.estado}
                                        onChange={handleInputChange}
                                    >
                                        {ESTADOS.map(estado => (
                                            <option key={estado.value} value={estado.value}>{estado.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Monto Bruto * <FaCalculator /></label>
                                    <input
                                        type="number"
                                        name="monto_bruto"
                                        value={formData.monto_bruto}
                                        onChange={handleInputChange}
                                        required
                                        min="0"
                                        step="1"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Tasa Retención (%) *</label>
                                    <input
                                        type="number"
                                        name="tasa_retencion"
                                        value={formData.tasa_retencion}
                                        onChange={handleInputChange}
                                        required
                                        min="0"
                                        max="100"
                                        step="0.01"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Monto Retención (calculado)</label>
                                    <input
                                        type="number"
                                        name="monto_retencion"
                                        value={formData.monto_retencion}
                                        readOnly
                                        className="readonly"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Monto Líquido (calculado)</label>
                                    <input
                                        type="number"
                                        name="monto_liquido"
                                        value={formData.monto_liquido}
                                        readOnly
                                        className="readonly"
                                    />
                                </div>

                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={resetForm}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingId ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RetencionHonorariosPage;
